import { FormInput } from "@components/FormInput/FormInput";
import { FORM_ACTIONS } from "@models/FormActions";
import { useConfirmationModalStore } from "@stores/confirmationModalStore";
import { onErrorShow } from "@utils/onErrorShow";
import { useEffect, useReducer, useState } from "react";
import { onInfoShow } from "@utils/onInfoShow";
import { useAuth } from "@Auth/useAuth";
import { useDrawer } from "@hooks/useDrawer";
import { DRAWER_CONTEXT } from "@models/DrawerContext";
import { useOrdersStore } from "@stores/ordersStore";
import { useProducts } from "@features/products/queries/useProducts";
import { useStockCommitmentsStore } from "@stores/stockCommitmentsStore";
import { useCustomersStore } from "@stores/customersStore";
import { Entity } from "@models/Entity";
import { returnErrorMessage } from "@utils/returnErrorMessage";
import { Section } from "@common/SimpleDrawer/components/Section";
import { Footer } from "@common/SimpleDrawer/components/Footer";
import { FormAutocomplete } from "@components/FormAutocomplete/FormAutoComplete";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Switch,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { CreateCustomerDrawer } from "./CreateCustomerDrawer";
import {
  CustomFieldsClient,
  ICustomFieldDefinition,
  ICustomFieldValue,
} from "@Clients/CustomFieldsClient";
import { Dropdown, DropdownOption } from "@components/Dropdown/Dropdown";

/**
 * Order status type union
 */
type OrderStatus = "open" | "processing" | "completed" | "cancelled";

/**
 * Stock warning type
 */
interface StockWarning {
  type: "low" | "insufficient";
  message: string;
  available: number;
  productName?: string;
}

/**
 * Form autocomplete option type
 */
interface AutocompleteOption {
  label: string;
  value: string;
}

type OrderInitialState = {
  use_auto_order_number: boolean;
  order_number: string;
  customer_id: string | null;
  notes: string;
  status: OrderStatus;
  order_items: OrderItemForm[];
  errors: Record<string, string>;
  stockWarnings: Record<string, StockWarning>;
  po_number: string;
  due_date: string;
  customer_reference: string;
  customFields: Record<string, string | number | boolean | null | undefined>;
};

type OrderItemForm = {
  id: string;
  product_id: string;
  quantity_requested: number;
  notes?: string;
};

// Initial State
const initialState: OrderInitialState = {
  use_auto_order_number: true,
  order_number: "",
  customer_id: null,
  notes: "",
  status: "open",
  order_items: [],
  errors: {} as Record<string, string>,
  stockWarnings: {},
  po_number: "",
  due_date: "",
  customer_reference: "",
  customFields: {},
};

interface Action<T> {
  type: FORM_ACTIONS;
  field?: keyof T;
  value?: T[keyof T];
  errors?: Record<string, string>;
  stockWarnings?: Record<string, StockWarning>;
}

// Reducer Function
function formReducer(
  state: OrderInitialState,
  action: Action<OrderInitialState>
) {
  switch (action.type) {
    case FORM_ACTIONS.SET_FIELD: {
      if (!action.field) return state;
      return {
        ...state,
        [action.field!]: action.value,
        errors: {
          ...state.errors,
          [action.field!]: "",
        },
      };
    }
    case FORM_ACTIONS.SET_ERRORS:
      return {
        ...state,
        errors: action.errors || {},
      };
    case FORM_ACTIONS.SET_STOCK_WARNINGS:
      console.log("Reducer: Setting stock warnings:", action.stockWarnings);
      return {
        ...state,
        stockWarnings: action.stockWarnings || {},
      };
    case FORM_ACTIONS.RESET:
      return initialState;
    default:
      throw new Error(`Unknown action type: ${action.type}`);
  }
}

interface OrderFormProps {
  handleClose: () => void;
}

export const OrderForm = ({ handleClose }: OrderFormProps) => {
  const [state, dispatch] = useReducer(
    formReducer as React.Reducer<
      Partial<OrderInitialState>,
      Action<OrderInitialState>
    >,
    initialState
  );

  const { setTouch } = useConfirmationModalStore();
  const { baseUser } = useAuth();

  const {
    close: closeOnSubmit,
    context,
    selectedId,
  } = useDrawer();
  const isEditing = context === DRAWER_CONTEXT.EDIT_ORDER;

  const { orders, getOrdersByOrganizationId, getOrderById } = useOrdersStore();
  const { data: products = [] } = useProducts();
  const { checkStockAvailability, getStockCommitmentsByOrderItemId } =
    useStockCommitmentsStore();
  const { customers, getCustomersByOrganizationId } = useCustomersStore();
  const [customColumns, setCustomColumns] = useState<ICustomFieldDefinition[]>(
    []
  );
  const [openCustomerCreate, setOpenCustomerCreate] = useState(false);

  const getCustomFieldValue = (value: ICustomFieldValue) => {
    if (value.value_text !== null && value.value_text !== undefined)
      return value.value_text;
    if (value.value_number !== null && value.value_number !== undefined)
      return value.value_number;
    if (value.value_bool !== null && value.value_bool !== undefined)
      return value.value_bool;
    if (value.value_date !== null && value.value_date !== undefined)
      return value.value_date;
    if (value.value_json !== null && value.value_json !== undefined)
      return value.value_json;
    return null;
  };

  // Debug stock warnings
  useEffect(() => {
    console.log("Stock warnings state changed:", state.stockWarnings);
  }, [state.stockWarnings]);

  // Fetch custom columns on mount
  useEffect(() => {
    const fetchCustomColumns = async () => {
      if (baseUser?.organization_id) {
        try {
          const columns = await CustomFieldsClient.getDefinitionsByOrganizationId(
            baseUser.organization_id,
            "order"
          );
          setCustomColumns(columns);
        } catch (error) {
          console.error("Error fetching custom columns:", error);
        }
      }
    };
    fetchCustomColumns();
  }, [baseUser?.organization_id]);

  // Load customers on mount
  useEffect(() => {
    if (baseUser?.organization_id) {
      getCustomersByOrganizationId(baseUser.organization_id, false);
    }
  }, [baseUser?.organization_id, getCustomersByOrganizationId]);

  // Load existing order details if editing
  useEffect(() => {
    if (context === DRAWER_CONTEXT.EDIT_ORDER && selectedId) {
      const order = orders.find((order) => order.id === selectedId);
      if (order) {
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "order_number",
          value: order.order_number || "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "use_auto_order_number",
          value: false,
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "customer_id",
          value: order.customer_id || null,
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "notes",
          value: order.notes || "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "status",
          value: order.status,
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "po_number",
          value: order.po_number || "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "due_date",
          value: order.due_date ? order.due_date.split("T")[0] : "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "customer_reference",
          value: order.customer_reference || "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "order_items",
          value: order.order_items.map((item) => ({
            id: item.id,
            product_id: item.product_id,
            quantity_requested: item.quantity_requested,
            notes: item.notes || "",
          })),
        });
      }
    }
  }, [context, selectedId, orders]);

  useEffect(() => {
    const fetchCustomFieldValues = async () => {
      if (
        context !== DRAWER_CONTEXT.EDIT_ORDER ||
        !selectedId ||
        !baseUser?.organization_id ||
        customColumns.length === 0
      ) {
        return;
      }
      try {
        const values = await CustomFieldsClient.getValuesByEntity(
          baseUser.organization_id,
          "order",
          selectedId
        );
        const definitionMap = new Map(
          customColumns.map((column) => [column.id, column.name])
        );
        const mappedValues: Record<
          string,
          string | number | boolean | null | undefined
        > = {};
        values.forEach((value) => {
          const fieldName = definitionMap.get(value.field_id);
          if (!fieldName) return;
          mappedValues[fieldName] = getCustomFieldValue(value) as
            | string
            | number
            | boolean
            | null
            | undefined;
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "customFields",
          value: mappedValues,
        });
      } catch (error) {
        console.error("Error fetching order custom field values:", error);
      }
    };
    fetchCustomFieldValues();
  }, [
    baseUser?.organization_id,
    context,
    customColumns,
    selectedId,
  ]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: name as keyof OrderInitialState,
      value,
    });
    setTouch(true);
  };

  const handleOrderNumberToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const useAuto = e.target.checked;
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: "use_auto_order_number",
      value: useAuto,
    });
    if (useAuto) {
      dispatch({
        type: FORM_ACTIONS.SET_FIELD,
        field: "order_number",
        value: "",
      });
    }
    setTouch(true);
  };

  const handleCustomerChange = (customerId: string) => {
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: "customer_id",
      value: customerId,
    });
    setTouch(true);
  };

  const handleCustomFieldChange = (
    fieldName: string,
    value: string | number | boolean | null | undefined
  ) => {
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: "customFields",
      value: {
        ...state.customFields,
        [fieldName]: value,
      },
    });
    setTouch(true);
  };

  const handleAddOrderItem = () => {
    const newItem: OrderItemForm = {
      id: `temp-${Date.now()}`,
      product_id: "",
      quantity_requested: 1,
      notes: "",
    };

    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: "order_items",
      value: [...(state.order_items || []), newItem],
    });
  };

  const handleRemoveOrderItem = (itemId: string) => {
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: "order_items",
      value: state.order_items?.filter((item) => item.id !== itemId) || [],
    });
  };

  const handleOrderItemChange = async (
    itemId: string,
    field: keyof OrderItemForm,
    value: OrderItemForm[keyof OrderItemForm]
  ) => {
    const updatedItems =
      state.order_items?.map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item
      ) || [];

    // If quantity or product changed, check stock availability and show warnings
    if (field === "quantity_requested" || field === "product_id") {
      const updatedItem = updatedItems.find((item) => item.id === itemId);
      if (
        updatedItem &&
        updatedItem.product_id &&
        updatedItem.quantity_requested > 0
      ) {
        try {
          let additionalNeeded = updatedItem.quantity_requested;
          let committedQuantity = 0;
          const isExistingItem = !updatedItem.id.startsWith("temp-");
          if (context === DRAWER_CONTEXT.EDIT_ORDER && isExistingItem && field !== "product_id") {
            const commitments = await getStockCommitmentsByOrderItemId(updatedItem.id);
            committedQuantity = commitments.reduce((sum, commitment) => {
              if (commitment.status !== "active") return sum;
              return sum + commitment.quantity_committed;
            }, 0);
            additionalNeeded = Math.max(
              0,
              updatedItem.quantity_requested - committedQuantity
            );
          }

          if (additionalNeeded === 0) {
            const newWarnings = { ...state.stockWarnings };
            delete newWarnings[itemId];
            dispatch({
              type: FORM_ACTIONS.SET_STOCK_WARNINGS,
              stockWarnings: newWarnings,
            });
            return;
          }

          console.log(
            "Checking stock for product:",
            updatedItem.product_id,
            "quantity:",
            additionalNeeded
          );
          const stockCheck = await checkStockAvailability(
            updatedItem.product_id,
            additionalNeeded
          );
          console.log("Stock check result:", stockCheck);

          const productName =
            products.find((p) => p.id === updatedItem.product_id)?.name ||
            "Selected product";
          const newWarnings = { ...state.stockWarnings };

          if (!stockCheck.available) {
            // Insufficient stock warning
            const committedLabel =
              committedQuantity > 0
                ? `Committed to this order: ${committedQuantity}\n`
                : "";
            newWarnings[itemId] = {
              type: "insufficient",
              message: `${committedLabel}Available: ${stockCheck.availableQuantity}, Requested: ${additionalNeeded}`,
              available: stockCheck.availableQuantity,
              productName,
            };
          } else if (stockCheck.availableQuantity < 10) {
            // Low stock warning (less than 10 items)
            newWarnings[itemId] = {
              type: "low",
              message: `Low stock for ${productName}. Only ${stockCheck.availableQuantity} items available`,
              available: stockCheck.availableQuantity,
              productName,
            };
          } else {
            // Remove warning if stock is sufficient
            delete newWarnings[itemId];
          }

          console.log("Dispatching stock warnings:", newWarnings);
          dispatch({
            type: FORM_ACTIONS.SET_STOCK_WARNINGS,
            stockWarnings: newWarnings,
          });
        } catch (error) {
          console.error("Error checking stock availability:", error);
        }
      }
    }

    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: "order_items",
      value: updatedItems,
    });
  };

  const handleFormVerification = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    const errors = {} as Record<string, string>;

    if (
      (isEditing || !state.use_auto_order_number) &&
      !state.order_number?.trim()
    ) {
      errors.order_number = "Order number is required";
    }

    if (!state.customer_id) {
      errors.customer_id = "Customer is required";
    }

    if (!state.order_items || state.order_items.length === 0) {
      errors.order_items = "At least one order item is required";
    } else {
      // Check stock availability for each item
      for (const [index, item] of state.order_items.entries()) {
        if (!item.product_id) {
          errors[`order_item_${index}_product`] = "Product is required";
        }
        if (!item.quantity_requested || item.quantity_requested <= 0) {
          errors[`order_item_${index}_quantity`] =
            "Quantity must be greater than 0";
        }
      }
    }

    // Validate required custom fields
    for (const column of customColumns) {
      if (column.required) {
        const fieldValue = state.customFields?.[column.name];
        if (
          !fieldValue ||
          (typeof fieldValue === "string" && !fieldValue.trim())
        ) {
          errors[`custom_${column.name}`] = `${column.label} is required`;
        }
      }
    }

    if (Object.keys(errors).length > 0) {
      dispatch({ type: FORM_ACTIONS.SET_ERRORS, errors });
      onErrorShow("Please fix the errors before submitting");
      return;
    }
    return true;
  };

  const handleSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    try {
      const validForm = await handleFormVerification(e);

      if (!validForm) return;

      const {
        use_auto_order_number,
        order_number,
        customer_id,
        notes,
        status,
        order_items,
        po_number,
        due_date,
        customer_reference,
        customFields,
      } = state;
      const manualOrderNumber = use_auto_order_number
        ? undefined
        : order_number?.trim() || undefined;

      const buildCustomFieldPayload = (entityId: string) => {
        const values = customColumns
          .map((column) => {
            const rawValue = customFields?.[column.name];
            const isEmptyValue =
              rawValue === null ||
              rawValue === undefined ||
              (typeof rawValue === "string" && rawValue.trim() === "");
            if (isEmptyValue) return null;

            const baseValue = {
              organization_id: baseUser!.organization_id!,
              entity_type: "order" as const,
              entity_id: entityId,
              field_id: column.id,
              value_text: null,
              value_number: null,
              value_bool: null,
              value_date: null,
              value_json: null,
            };

            switch (column.type) {
              case "text":
              case "textarea":
              case "select":
                return { ...baseValue, value_text: String(rawValue) };
              case "number":
                return {
                  ...baseValue,
                  value_number:
                    rawValue === "" ? null : Number(rawValue as number),
                };
              case "boolean":
                return { ...baseValue, value_bool: Boolean(rawValue) };
              case "date":
                return { ...baseValue, value_date: String(rawValue) };
              default:
                return { ...baseValue, value_text: String(rawValue) };
            }
          })
          .filter(Boolean) as Array<
            Omit<ICustomFieldValue, "id" | "created_at" | "updated_at">
          >;

        return values;
      };

      if (context === DRAWER_CONTEXT.EDIT_ORDER && selectedId) {
        // **Update existing order**
        const existingOrder = orders.find((o) => o.id === selectedId);
        await useOrdersStore.getState().updateOrder({
          id: selectedId,
          order_number:
            order_number?.trim() || existingOrder?.order_number || "",
          customer_id: customer_id || null,
          // Database trigger will populate customer_name and customer_email automatically
          // Only include them if customer_id is null (legacy support)
          customer_name: customer_id ? "" : existingOrder?.customer_name || "",
          customer_email: customer_id
            ? undefined
            : existingOrder?.customer_email,
          notes: notes || "",
          status: status!,
          created_by: baseUser!.id,
          organization_id: baseUser!.organization_id!,
          backorder_status: existingOrder?.backorder_status || "none",
          created_at: existingOrder?.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
          po_number: po_number || undefined,
          due_date: due_date ? new Date(due_date).toISOString() : undefined,
          customer_reference: customer_reference || undefined,
        });
        await CustomFieldsClient.deleteValuesForEntity(
          baseUser!.organization_id,
          "order",
          selectedId
        );
        const values = buildCustomFieldPayload(selectedId);
        if (values.length > 0) {
          await CustomFieldsClient.upsertValues(values);
        }

        // Get the original order to compare items
        const originalOrder = orders.find((o) => o.id === selectedId);
        const originalItemIds =
          originalOrder?.order_items?.map((item) => item.id) || [];
        const currentItemIds = order_items?.map((item) => item.id) || [];

        // Find items that were deleted (existed before but not in current form)
        const deletedItemIds = originalItemIds.filter(
          (id) => !currentItemIds.includes(id)
        );

        // Delete removed items
        for (const itemId of deletedItemIds) {
          await useOrdersStore.getState().deleteOrderItem(itemId);
        }

        // Update order items
        const { BackorderQueueClient } = await import(
          "@Clients/BackorderQueueClient"
        );
        const { useStockCommitmentsStore } = await import(
          "@stores/stockCommitmentsStore"
        );
        const stockCommitmentsStore = useStockCommitmentsStore.getState();

        for (const item of order_items || []) {
          if (item.id.startsWith("temp-")) {
            // Create new item
            const orderItem = await useOrdersStore.getState().createOrderItem({
              order_id: selectedId,
              product_id: item.product_id,
              quantity_requested: item.quantity_requested,
              quantity_fulfilled: 0,
              status: "pending",
              notes: item.notes || "",
              backorder_quantity: 0,
              fulfillment_priority: 3,
              organization_id: baseUser!.organization_id!,
            });

            try {
              const stockCheck = await checkStockAvailability(
                item.product_id,
                item.quantity_requested
              );

              if (stockCheck.available) {
                await stockCommitmentsStore.commitStock(
                  selectedId,
                  orderItem.id,
                  item.product_id,
                  item.quantity_requested,
                  baseUser?.organization_id || ""
                );
              } else {
                await BackorderQueueClient.createBackorderQueue({
                  order_id: selectedId,
                  order_item_id: orderItem.id,
                  product_id: item.product_id,
                  quantity_needed: item.quantity_requested,
                  priority: 3,
                  organization_id: baseUser?.organization_id || "",
                });
              }
            } catch (error) {
              console.error("Error checking stock availability:", error);
              await BackorderQueueClient.createBackorderQueue({
                order_id: selectedId,
                order_item_id: orderItem.id,
                product_id: item.product_id,
                quantity_needed: item.quantity_requested,
                priority: 3,
                organization_id: baseUser?.organization_id || "",
              });
            }
          } else {
            // Update existing item
            await useOrdersStore.getState().updateOrderItem({
              id: item.id,
              order_id: selectedId,
              product_id: item.product_id,
              quantity_requested: item.quantity_requested,
              quantity_fulfilled: 0,
              status: "pending",
              notes: item.notes || "",
              backorder_quantity: 0,
              fulfillment_priority: 3,
              organization_id: baseUser!.organization_id!,
            });
          }
        }

        // Refresh the orders list and the specific order
        await getOrdersByOrganizationId(baseUser?.organization_id || "", false);
        // Also refresh the specific order to get updated order items
        await getOrderById(selectedId);
        onInfoShow("Order updated successfully");
      } else {
        // **Create a new order**
        // return;
        const newOrder = await useOrdersStore.getState().createOrder({
          ...(manualOrderNumber ? { order_number: manualOrderNumber } : {}),
          customer_id: customer_id || null,
          // Database trigger will populate customer_name and customer_email automatically
          // Only include them if customer_id is null (legacy support)
          customer_name: customer_id ? "" : "",
          customer_email: customer_id ? undefined : undefined,
          notes: notes || "",
          status: status!,
          created_by: baseUser?.id || "",
          organization_id: baseUser?.organization_id || "",
          backorder_status: "none",
          po_number: po_number || undefined,
          due_date: due_date ? new Date(due_date).toISOString() : undefined,
          customer_reference: customer_reference || undefined,
        });
        const values = buildCustomFieldPayload(newOrder.id);
        if (values.length > 0) {
          await CustomFieldsClient.upsertValues(values);
        }

        // Create order items with conditional stock commitment logic
        const { BackorderQueueClient } = await import(
          "@Clients/BackorderQueueClient"
        );

        for (const item of order_items || []) {
          // Create the order item first
          const orderItem = await useOrdersStore.getState().createOrderItem({
            order_id: newOrder.id,
            product_id: item.product_id,
            quantity_requested: item.quantity_requested,
            quantity_fulfilled: 0,
            status: "pending",
            notes: item.notes || "",
            backorder_quantity: 0,
            fulfillment_priority: 3,
            organization_id: baseUser!.organization_id!,
          });

          // Check stock availability for this item
          try {
            const stockCheck = await checkStockAvailability(
              item.product_id,
              item.quantity_requested
            );

            console.log("stockCheck", stockCheck);

            if (stockCheck.available) {
              // Stock is available - create stock commitment immediately
              const { useStockCommitmentsStore } = await import(
                "@stores/stockCommitmentsStore"
              );
              const stockCommitmentsStore = useStockCommitmentsStore.getState();

              await stockCommitmentsStore.commitStock(
                newOrder.id,
                orderItem.id,
                item.product_id,
                item.quantity_requested,
                baseUser?.organization_id || ""
              );

              // No backorder queue entry needed - stock is available
            } else {
              const { useStockCommitmentsStore } = await import(
                "@stores/stockCommitmentsStore"
              );
              const stockCommitmentsStore = useStockCommitmentsStore.getState();
              const commitQuantity = Math.max(0, stockCheck.availableQuantity);
              const shortfall = Math.max(
                0,
                item.quantity_requested - commitQuantity
              );

              if (commitQuantity > 0) {
                await stockCommitmentsStore.commitStock(
                  newOrder.id,
                  orderItem.id,
                  item.product_id,
                  commitQuantity,
                  baseUser?.organization_id || ""
                );
              }

              if (shortfall > 0) {
                // Create backorder for the remaining quantity
                await BackorderQueueClient.createBackorderQueue({
                  order_id: newOrder.id,
                  order_item_id: orderItem.id,
                  product_id: item.product_id,
                  quantity_needed: shortfall,
                  priority: 3,
                  organization_id: baseUser?.organization_id || "",
                });
              }
            }
          } catch (error) {
            console.error("Error checking stock availability:", error);
            // If stock check fails, create backorder queue entry as fallback
            await BackorderQueueClient.createBackorderQueue({
              order_id: newOrder.id,
              order_item_id: orderItem.id,
              product_id: item.product_id,
              quantity_needed: item.quantity_requested,
              priority: 3,
              organization_id: baseUser?.organization_id || "",
            });
          }
        }

        getOrdersByOrganizationId(baseUser?.organization_id || "", false);
        onInfoShow("Order created successfully");
      }

      closeOnSubmit();
      dispatch({ type: FORM_ACTIONS.RESET });
    } catch (error: unknown) {
      const rawErrorMessage =
        typeof error === "string"
          ? error
          : (error as { message?: string; details?: string })?.message ||
          (error as { details?: string })?.details ||
          "";
      const errorMessage =
        returnErrorMessage(Entity.ORDERS, rawErrorMessage) ||
        "Failed to save order";
      if (rawErrorMessage.includes("unique_order_number_per_org")) {
        dispatch({
          type: FORM_ACTIONS.SET_ERRORS,
          errors: {
            ...(state.errors || {}),
            order_number: "Order number is already in use",
          },
        });
      }
      onErrorShow(errorMessage);
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, overflow: "auto", pb: 2 }}>
        <Section>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
            }}
          >
            <Switch
              size="small"
              checked={state.use_auto_order_number || false}
              onChange={handleOrderNumberToggle}
              disabled={isEditing}
            />
            <Typography variant="body2" color="text.secondary">
              Auto-generate order number
            </Typography>
          </Box>
          <FormInput
            name="order_number"
            label="Order Number"
            onChange={handleChange}
            value={state.order_number || ""}
            errorMessage={state.errors?.order_number}
            disabled={state.use_auto_order_number && !isEditing}
            placeholder={
              state.use_auto_order_number && !isEditing
                ? "Auto-generated on save"
                : ""
            }
          />
          <FormAutocomplete
            label="Customer"
            dropdownPlaceholder="Select a customer"
            value={state.customer_id || ""}
            onChange={handleCustomerChange}
            errorMessage={state.errors?.customer_name}
            options={customers.map(
              (customer): AutocompleteOption => ({
                label: customer.email
                  ? `${customer.name} (${customer.email})`
                  : customer.name,
                value: customer.id,
              })
            )}
            actionButtonText="New Customer"
            actionButtonOnClick={() => {
              setOpenCustomerCreate(true);
            }}
          />
          <FormInput
            name="po_number"
            label="PO Number"
            onChange={handleChange}
            value={state.po_number || ""}
            errorMessage={state.errors?.po_number}
          />
          <FormInput
            name="customer_reference"
            label="Customer Reference"
            onChange={handleChange}
            value={state.customer_reference || ""}
            errorMessage={state.errors?.customer_reference}
            placeholder="Job name, project code, etc."
          />
          <FormInput
            name="due_date"
            label="Due Date"
            type="date"
            onChange={handleChange}
            value={state.due_date || ""}
            errorMessage={state.errors?.due_date}
          />
          <FormInput
            name="notes"
            label="Order Notes"
            onChange={handleChange}
            value={state.notes || ""}
            errorMessage={state.errors?.notes}
          />

          {/* Custom Fields - integrated with regular fields */}
          {customColumns
            .sort((a, b) => a.display_order - b.display_order)
            .map((column) => {
              const fieldValue =
                state.customFields?.[column.name] || column.default_value || "";

              // Render based on field type
              if (column.type === "text") {
                return (
                  <FormInput
                    key={column.id}
                    name={`custom_${column.name}`}
                    label={column.label}
                    onChange={(e) =>
                      handleCustomFieldChange(column.name, e.target.value)
                    }
                    value={fieldValue as string}
                    placeholder={column.placeholder || ""}
                    errorMessage={
                      column.required && !fieldValue
                        ? `${column.label} is required`
                        : state.errors?.[`custom_${column.name}`]
                    }
                  />
                );
              }

              if (column.type === "textarea") {
                return (
                  <Box key={column.id} sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {column.label}
                      {column.required && (
                        <Typography component="span" color="error">
                          {" *"}
                        </Typography>
                      )}
                    </Typography>
                    <textarea
                      name={`custom_${column.name}`}
                      value={fieldValue as string}
                      onChange={(e) =>
                        handleCustomFieldChange(column.name, e.target.value)
                      }
                      placeholder={column.placeholder || ""}
                      rows={4}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: `1px solid ${state.errors?.[`custom_${column.name}`]
                          ? "#f3b0a2"
                          : "#e0e0e0"
                          }`,
                        fontSize: "14px",
                        fontFamily: "inherit",
                        resize: "vertical",
                        minHeight: "80px",
                      }}
                    />
                    {state.errors?.[`custom_${column.name}`] && (
                      <Typography
                        variant="subtitle1"
                        color="error"
                        sx={{ mt: 0.5 }}
                      >
                        {state.errors[`custom_${column.name}`]}
                      </Typography>
                    )}
                    {column.help_text && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {column.help_text}
                      </Typography>
                    )}
                  </Box>
                );
              }

              if (column.type === "number") {
                return (
                  <FormInput
                    key={column.id}
                    name={`custom_${column.name}`}
                    label={column.label}
                    type="number"
                    onChange={(e) =>
                      handleCustomFieldChange(
                        column.name,
                        e.target.value === "" ? null : parseFloat(e.target.value)
                      )
                    }
                    value={fieldValue as string | number}
                    placeholder={column.placeholder || ""}
                    errorMessage={
                      column.required &&
                        (fieldValue === "" ||
                          fieldValue === null ||
                          fieldValue === undefined)
                        ? `${column.label} is required`
                        : state.errors?.[`custom_${column.name}`]
                    }
                  />
                );
              }

              if (column.type === "date") {
                let dateValue = "";
                if (fieldValue) {
                  if (typeof fieldValue === "string") {
                    dateValue = fieldValue.includes("T")
                      ? fieldValue.split("T")[0]
                      : fieldValue;
                  } else {
                    // Convert other types to string, then try to format as date
                    const dateStr = String(fieldValue);
                    dateValue = dateStr.includes("T")
                      ? dateStr.split("T")[0]
                      : dateStr;
                  }
                }
                return (
                  <FormInput
                    key={column.id}
                    name={`custom_${column.name}`}
                    label={column.label}
                    type="date"
                    onChange={(e) =>
                      handleCustomFieldChange(column.name, e.target.value)
                    }
                    value={dateValue}
                    placeholder={column.placeholder || ""}
                    errorMessage={
                      column.required && !fieldValue
                        ? `${column.label} is required`
                        : state.errors?.[`custom_${column.name}`]
                    }
                  />
                );
              }

              if (column.type === "boolean") {
                return (
                  <Box key={column.id} sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <input
                        type="checkbox"
                        checked={fieldValue === true || fieldValue === "true"}
                        onChange={(e) =>
                          handleCustomFieldChange(column.name, e.target.checked)
                        }
                        style={{ marginRight: 8 }}
                      />
                      <Typography variant="body2">{column.label}</Typography>
                      {column.required && (
                        <Typography variant="caption" color="error">
                          *
                        </Typography>
                      )}
                    </Box>
                    {column.help_text && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 4, display: "block" }}
                      >
                        {column.help_text}
                      </Typography>
                    )}
                  </Box>
                );
              }

              if (column.type === "select" && column.field_options) {
                const selectOptions: DropdownOption[] =
                  column.field_options.map((opt) => ({
                    value: opt.value,
                    label: opt.label,
                  }));

                return (
                  <Box key={column.id} sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {column.label}
                      {column.required && (
                        <Typography component="span" color="error">
                          {" *"}
                        </Typography>
                      )}
                    </Typography>
                    <Dropdown
                      options={selectOptions}
                      value={fieldValue as string}
                      onChange={(value) =>
                        handleCustomFieldChange(column.name, value)
                      }
                      placeholder={
                        column.placeholder || `Select ${column.label}`
                      }
                      variant="outlined"
                      size="small"
                    />
                    {column.help_text && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {column.help_text}
                      </Typography>
                    )}
                  </Box>
                );
              }

              return null;
            })}
        </Section>

        <Section>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h5">Order Items</Typography>
            <Button
              startIcon={<Add />}
              onClick={handleAddOrderItem}
              variant="outlined"
              size="small"
            >
              Add Item
            </Button>
          </Box>

          {state.order_items?.map((item, index) => (
            <Box
              key={item.id}
              sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 2, mb: 2 }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="subtitle2">Item {index + 1}</Typography>
                <IconButton
                  onClick={() => handleRemoveOrderItem(item.id)}
                  size="small"
                  color="error"
                >
                  <Delete />
                </IconButton>
              </Box>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <FormAutocomplete
                    dropdownPlaceholder="Select a product"
                    label="Product"
                    onChange={(productId) =>
                      handleOrderItemChange(item.id, "product_id", productId)
                    }
                    value={item.product_id}
                    errorMessage={state.errors?.[`order_item_${index}_product`]}
                    options={products.map(
                      (product): AutocompleteOption => ({
                        label: `${product.name} (${product.sku})`,
                        value: product.id,
                      })
                    )}
                  />
                </Box>

                <Box sx={{ minWidth: 120 }}>
                  <FormInput
                    name={`quantity_${item.id}`}
                    label="Quantity"
                    type="number"
                    onChange={(e) =>
                      handleOrderItemChange(
                        item.id,
                        "quantity_requested",
                        parseInt(e.target.value) || 0
                      )
                    }
                    value={item.quantity_requested.toString()}
                    errorMessage={
                      state.errors?.[`order_item_${index}_quantity`]
                    }
                  />
                </Box>
              </Box>

              {/* Stock Warning Display */}
              {state.stockWarnings?.[item.id] && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor:
                      state.stockWarnings[item.id].type === "insufficient"
                        ? "error.light"
                        : "warning.light",
                    border: `1px solid ${state.stockWarnings[item.id].type === "insufficient"
                      ? "error.main"
                      : "warning.main"
                      }`,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        state.stockWarnings[item.id].type === "insufficient"
                          ? "error.main"
                          : "warning.main",
                      fontWeight: 500,
                    }}
                  >
                    ⚠️ {state.stockWarnings[item.id].message}
                  </Typography>
                  {state.stockWarnings[item.id].type === "insufficient" && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "error.main",
                        display: "block",
                        mt: 0.5,
                      }}
                    >
                      This order will create a backorder situation.
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{ mt: 1 }}>
                <FormInput
                  name={`notes_${item.id}`}
                  label="Item Notes"
                  onChange={(e) =>
                    handleOrderItemChange(item.id, "notes", e.target.value)
                  }
                  value={item.notes || ""}
                />
              </Box>
            </Box>
          ))}

          {state.errors?.order_items && (
            <Typography
              color="error"
              variant="caption"
              sx={{ mt: 1, display: "block" }}
            >
              {state.errors.order_items}
            </Typography>
          )}

          {/* Overall Stock Warning Summary */}
          {Object.keys(state.stockWarnings || {}).length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 1,
                backgroundColor: "warning.light",
                border: "1px solid warning.main",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: "warning.main", fontWeight: 600, mb: 1 }}
              >
                ⚠️ Stock Warnings
              </Typography>
              <Typography variant="body2" sx={{ color: "warning.main", mb: 1 }}>
                This order has stock availability issues:
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {Object.values(state.stockWarnings || {}).map(
                  (warning, index) => (
                    <li key={index}>
                      <Typography
                        variant="body2"
                        sx={{ color: "warning.main" }}
                      >
                        {warning.productName
                          ? `${warning.productName}: ${warning.message}`
                          : warning.message}
                      </Typography>
                    </li>
                  )
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{ color: "warning.main", display: "block", mt: 1 }}
              >
                You can still create this order, but consider the stock
                implications.
              </Typography>
            </Box>
          )}
        </Section>
      </Box>

      <Footer handleClose={handleClose} handleSubmit={handleSubmit} />
      <CreateCustomerDrawer
        onClose={() => {
          setOpenCustomerCreate(false);
        }}
        open={openCustomerCreate}
        onCustomerCreated={(customerId) => {
          // Refresh customers list
          if (baseUser?.organization_id) {
            getCustomersByOrganizationId(baseUser.organization_id, false).then(
              () => {
                // Auto-select the newly created customer
                dispatch({
                  type: FORM_ACTIONS.SET_FIELD,
                  field: "customer_id",
                  value: customerId,
                });
                setOpenCustomerCreate(false);
              }
            );
          }
        }}
      />
    </Box>
  );
};
